import { Button, Form, Input } from "antd";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { dataParser } from "src/common/dataParser";
import { trimRequired } from "src/common/validationRules";
import { IdBox } from "src/components/IdBox";
import { PageSubheader } from "src/components/PageSubheader";
import { SafeForm } from "src/components/SafeForm/SafeForm";
import { ZRole, ZRoleSaveData, zRoleSaveData } from "src/types";

import * as styles from "./RoleForm.module.less";

const makeInitialData = (sourceData: ZRole): ZRoleSaveData =>
  dataParser(() => zRoleSaveData.parse(sourceData), "Role source data");

type PropsManagementRoleForm = {
  sourceData: ZRole | undefined;
  disabled: boolean;
  saving: boolean;
  extraButtons?: React.ReactNode;
  onSubmit: (data: ZRoleSaveData) => void;
  onCancel: () => void;
};

export const RoleForm: React.FC<PropsManagementRoleForm> = observer((props) => {
  const { sourceData, disabled, saving, extraButtons, onSubmit, onCancel } =
    props;
  const [form] = Form.useForm();
  const isNew = !sourceData?.id;
  const initialData = useMemo(
    () => (sourceData ? makeInitialData(sourceData) : undefined),
    [sourceData],
  );
  return (
    <SafeForm
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      disabled={disabled || saving}
      initialValues={initialData}
    >
      <PageSubheader
        title={
          isNew
            ? t("New [entity]", { entity: t("Role") })
            : t("Edit [entity]", { entity: t("Role") })
        }
      >
        <Button onClick={onCancel}>{t("Cancel")}</Button>
        {extraButtons}
        <Button type="primary" htmlType="submit" loading={saving}>
          {isNew ? t("Create") : t("Save")}
        </Button>
      </PageSubheader>

      <div className={styles.formBox}>
        <Form.Item
          name="name"
          label={t("Name")}
          rules={[trimRequired(t("Field required"))]}
        >
          <Input allowClear />
        </Form.Item>
        <IdBox entity={sourceData} />
        <Form.Item name="description" label={t("Description")}>
          <Input.TextArea allowClear rows={4} />
        </Form.Item>
      </div>
    </SafeForm>
  );
});
